/**
 * Shared rendering for activity entries (PROV-AUDIT).
 *
 * The server does the presentation mapping (labels, change pairs,
 * resolved entity names); this module only decides how those arrive on
 * screen, and it is shared by the Operator activity page and every
 * detail page's Activity section so an event cannot read differently
 * on two surfaces.
 */
import { Badge } from '@speaking-coach/ui'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type {
  ActivityEntity,
  ActivityEntityType,
  ActivityEntry,
  ActivityFamily,
  ActivityOperation,
} from './types.js'

export const ENTITY_TYPE_LABELS: Record<ActivityEntityType, string> = {
  client: 'Client',
  cohort: 'Cohort',
  student: 'Student',
  hr_user: 'HR user',
  admin: 'Admin',
  enrollment: 'Enrollment',
}

/** Change kinds — worded as KINDS OF CHANGE so the family filter cannot
 * be mistaken for the entity-type filter ("what was it about"). */
export const FAMILY_LABELS: Record<ActivityFamily, string> = {
  configuration: 'Client & cohort setup',
  identity: 'Identity & profile',
  enrollment: 'Enrollment lifecycle',
  invitation: 'Invitations',
  access: 'Access changes',
  deliverability: 'Email deliverability',
}

/** The verb axis. "Removed" is the investigation shape: deletions,
 * deactivations, cancellations, and endings across every entity kind. */
export const OPERATION_LABELS: Record<ActivityOperation, string> = {
  created: 'Created',
  changed: 'Changed',
  removed: 'Removed',
}

/**
 * The pivoted Activity page for one entity — the Django-style "History"
 * view: paginated, filterable, one consistent destination. Detail pages
 * link here from their header instead of embedding a timeline;
 * provenance is consulted on demand, not paid for on every page view.
 * `entityLabel` rides the URL so the pivot banner can name the thing
 * without a lookup (display-only; the server never reads it).
 */
export function activityPageHref(
  entityType: ActivityEntityType,
  entityId: string,
  clientId?: string,
  entityLabel?: string,
): string {
  const query = new URLSearchParams({ entityType, entityId })
  if (clientId !== undefined) query.set('clientId', clientId)
  if (entityLabel !== undefined && entityLabel !== '') {
    query.set('entityLabel', entityLabel)
  }
  return `/admin/activity?${query.toString()}`
}

/** Filter days are JST days — the operational timezone every Admin
 * timestamp already renders in. */
export function jstDayStart(day: string): string {
  return `${day}T00:00:00+09:00`
}

export function jstDayEnd(day: string): string {
  return `${day}T23:59:59.999+09:00`
}

/** Operational timezone — matches every other Admin timestamp. */
export function formatActivityTimestamp(value: string): string {
  return new Intl.DateTimeFormat('en', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Asia/Tokyo',
  }).format(new Date(value))
}

const MINUTE_MS = 60_000
const HOUR_MS = 3_600_000
const DAY_MS = 86_400_000

/**
 * "2 min. ago" / "3 days ago" — recency at a glance while scanning a
 * timeline; the exact JST instant stays underneath for the record.
 */
export function formatRelativeTime(value: string, now: number): string {
  const elapsed = now - Date.parse(value)
  if (elapsed < 45_000) return 'just now'
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'always', style: 'short' })
  if (elapsed < HOUR_MS) return rtf.format(-Math.round(elapsed / MINUTE_MS), 'minute')
  if (elapsed < DAY_MS) return rtf.format(-Math.round(elapsed / HOUR_MS), 'hour')
  if (elapsed < 7 * DAY_MS) return rtf.format(-Math.round(elapsed / DAY_MS), 'day')
  if (elapsed < 30 * DAY_MS) return rtf.format(-Math.round(elapsed / (7 * DAY_MS)), 'week')
  if (elapsed < 365 * DAY_MS) return rtf.format(-Math.round(elapsed / (30 * DAY_MS)), 'month')
  return rtf.format(-Math.round(elapsed / (365 * DAY_MS)), 'year')
}

/**
 * A once-a-minute clock so relative times on an open page do not
 * freeze at whatever they said on load. One hook per TABLE, not per
 * cell — the pages pass `now` down.
 */
export function useNow(intervalMs = MINUTE_MS): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(timer)
  }, [intervalMs])
  return now
}

/** The shared "When" cell: recency first, the exact instant beneath. */
export function ActivityWhen({
  occurredAt,
  now,
}: {
  occurredAt: string
  now: number
}): JSX.Element {
  return (
    <>
      <div>{formatRelativeTime(occurredAt, now)}</div>
      <div className="text-caption text-text-faint">
        {formatActivityTimestamp(occurredAt)}
      </div>
    </>
  )
}

/** Where an entity reference links; null when no Admin page owns it. */
export function activityEntityHref(entity: ActivityEntity): string | null {
  switch (entity.entityType) {
    case 'client':
      return `/admin/clients/${entity.entityId}`
    case 'cohort':
      return entity.clientId !== null
        ? `/admin/clients/${entity.clientId}/cohorts/${entity.entityId}`
        : null
    case 'student':
      return `/admin/students/${entity.entityId}/edit`
    case 'hr_user':
      return entity.clientId !== null
        ? `/admin/clients/${entity.clientId}/hr-users/${entity.entityId}`
        : null
    case 'admin':
      return `/admin/admins/${entity.entityId}`
    case 'enrollment':
      // No enrollment page exists; enrollments render inside the
      // student page. The entry's student entity carries that link.
      return null
  }
}

/**
 * A reference must never vanish just because its row did: an operator
 * reconstructing an incident needs "the (now removed) cohort 1a2b…",
 * not a silently shorter sentence.
 */
export function activityEntityLabel(entity: ActivityEntity): string {
  if (entity.label !== null) return entity.label
  const kind = ENTITY_TYPE_LABELS[entity.entityType]
  return `${kind} ${entity.entityId.slice(0, 8)}… (no longer resolves)`
}

export function ActivityActorCell({ entry }: { entry: ActivityEntry }): JSX.Element {
  if (entry.actorKind === 'system') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Badge variant="muted">System</Badge>
        <span className="text-text-secondary">{entry.actorLabel ?? 'System'}</span>
      </span>
    )
  }
  if (entry.actorKind === 'unknown') {
    // Pre-sign-in boundary writes (self-service recovery). Not "no
    // actor" — the request had an origin, just not an authenticated one.
    return <Badge variant="muted">Unauthenticated request</Badge>
  }
  const name =
    entry.actorLabel ?? `User ${entry.actorId.slice(0, 8)}… (no longer resolves)`
  if (entry.actorKind === 'user') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Badge variant="neutral">Self-service</Badge>
        <span>{name}</span>
      </span>
    )
  }
  return <span>{name}</span>
}

const ROLE_PREFIX: Partial<Record<ActivityEntity['role'], string>> = {
  from: 'from',
  to: 'to',
  added: '+',
  removed: '−',
}

export function ActivityEntityLinks({
  entities,
  skipEntityId,
}: {
  entities: ActivityEntity[]
  /** The page's own entity — naming it on its own timeline is noise. */
  skipEntityId?: string
}): JSX.Element | null {
  const shown = entities.filter((entity) => entity.entityId !== skipEntityId)
  if (shown.length === 0) return null
  return (
    <span className="text-text-secondary">
      {shown.map((entity, index) => {
        const href = activityEntityHref(entity)
        const label = activityEntityLabel(entity)
        const prefix = ROLE_PREFIX[entity.role]
        return (
          <span key={`${entity.entityId}-${entity.role}`}>
            {index > 0 && ' · '}
            {prefix !== undefined && `${prefix} `}
            {href !== null ? (
              <Link to={href} className="underline">
                {label}
              </Link>
            ) : (
              label
            )}
          </span>
        )
      })}
    </span>
  )
}

export function ActivityChangeSummary({ entry }: { entry: ActivityEntry }): JSX.Element | null {
  if (entry.changes.length === 0 && entry.summary === null) return null
  return (
    <div className="text-caption text-text-secondary space-y-0.5">
      {entry.changes.map((change) => (
        <div key={change.field}>
          {change.field}: {change.before ?? '—'} → {change.after ?? '—'}
        </div>
      ))}
      {entry.summary !== null && <div>{entry.summary}</div>}
    </div>
  )
}
